import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import {
  fetchConfig,
  cartAddJsonBody,
  setCartDiscountCodes,
  getOfferGiftCode,
  getSavedDiscountCodes,
  applyOfferDiscountCodes,
} from '@theme/utilities';

/**
 * @typedef {object} SubscriptionFormV6Refs
 * @property {HTMLFormElement} [form] - The add to cart form of the block.
 * @property {HTMLInputElement} [quantityInput] - Hidden quantity input.
 * @property {HTMLInputElement[]} [optionInputs] - Selling plan radio inputs of every option.
 * @property {HTMLButtonElement} [subscriptionButton] - Adds the selected subscription option.
 * @property {HTMLButtonElement} [onetimeButton] - Adds one unit without a subscription.
 * @property {HTMLElement} [buttonPrice] - Price inside the add to cart button.
 * @property {HTMLElement} [error] - Error message container.
 * @extends Component<SubscriptionFormV6Refs>
 */

/**
 * Subscription form v6.
 *
 * Self-contained: the block has its own form, adds the selected option (selling plan ×
 * quantity, plus the subscription gifts) or one unit as a one time purchase, and then
 * redirects to the checkout.
 */
class SubscriptionFormV6 extends Component {
  /** @type {string} */
  static SUBSCRIPTION = 'subscription';

  /** @type {string} */
  static ONETIME = 'onetime';

  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.parent = this.closest('[data-product]');

    // No form is rendered when the product is not set (a notice is shown in the editor instead).
    if (!this.refs.form) return;

    this.#selectDefaultOption();
    this.update();

    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Coming back from the checkout with the browser "back" button restores the page from the
   * back/forward cache in the state it was left — with the buttons still loading.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#setLoading(false, null);
    this.update();
  };

  /**
   * Radio change handler, wired declaratively in the option markup.
   */
  handleOptionChange() {
    this.update();
  }

  /**
   * Pushes the state of the selected option to the hidden inputs, the button and the gallery.
   */
  update() {
    const option = this.selectedOption;
    if (!option || !this.refs.quantityInput) return;

    this.refs.quantityInput.value = option.dataset.quantity || '1';

    if (this.refs.buttonPrice) {
      this.refs.buttonPrice.textContent = option.dataset.price || '';
    }

    const discountInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-subscription-discount-target]')
    );
    if (discountInput) {
      discountInput.value = option.dataset.discount || '';
    }

    this.#updateProductInfo(option);
    this.#updateFirstImage(option);
  }

  /**
   * Both buttons are submit buttons of the same form, the submitter tells the mode apart.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting || !this.refs.form) return;

    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);
    const mode = submitter?.value === SubscriptionFormV6.ONETIME ? SubscriptionFormV6.ONETIME : SubscriptionFormV6.SUBSCRIPTION;

    if (mode === SubscriptionFormV6.SUBSCRIPTION && !this.selectedOption) return;

    this.#setLoading(true, submitter);
    this.#hideError();

    // Offer gifts: remember the customer's codes before the cart is cleared, to set them back with the gift code
    const offerGiftCode = getOfferGiftCode(this.refs.form);
    /** @type {string[] | null} */
    const savedDiscountCodes =
      offerGiftCode === null ? null : await getSavedDiscountCodes(this.dataset.clearCart === 'true');

    try {
      if (this.dataset.clearCart === 'true') {
        await this.#clearCart();
      }

      const formData = this.#buildFormData(mode);
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify(cartAddJsonBody(formData)) })
      );
      const data = await response.json();

      if (!response.ok || data.status) {
        // The cart may already be cleared, so give the customer's codes back
        if (savedDiscountCodes?.length) {
          await setCartDiscountCodes(savedDiscountCodes).catch((error) => console.error(error));
        }

        throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      }

      // #buildFormData enabled the gift lines only for a subscription, so the gift code follows them
      if (savedDiscountCodes && offerGiftCode !== null) {
        await applyOfferDiscountCodes({
          form: this.refs.form,
          giftCode: offerGiftCode,
          savedCodes: savedDiscountCodes,
        });
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'subscription-form-v6',
          variantId: String(formData.get('id') ?? ''),
          willRedirect: true,
        })
      );

      window.location.href = this.dataset.checkoutUrl || '/checkout';
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : String(error));
      this.#setLoading(false, submitter);
    }
  }

  // GETTERS

  /**
   * @returns {HTMLElement | null} The wrapper of the checked option.
   */
  get selectedOption() {
    const checked = this.refs.optionInputs?.find((input) => input.checked);

    return checked?.closest('.sfv6-option') ?? null;
  }

  // HELPERS

  /**
   * Falls back to the first option when none of them is selected in the theme editor.
   */
  #selectDefaultOption() {
    const inputs = this.refs.optionInputs;

    if (!inputs?.length || inputs.some((input) => input.checked)) return;

    inputs[0].checked = true;
  }

  /**
   * Gift lines are only submitted together with a subscription, the one time
   * purchase is always a single unit without a selling plan.
   * @param {string} mode
   * @returns {FormData}
   */
  #buildFormData(mode) {
    const isSubscription = mode === SubscriptionFormV6.SUBSCRIPTION;

    for (const field of this.querySelectorAll('[data-subscribe-gift-field], [data-offer-gift-field]')) {
      field.toggleAttribute('disabled', !isSubscription);
    }

    const formData = new FormData(this.refs.form);

    if (!isSubscription) {
      formData.delete('selling_plan');
      formData.set('quantity', '1');
    }

    return formData;
  }

  async #clearCart() {
    await fetch('/cart/clear.js', {
      ...fetchConfig('json'),
      body: null,
    });

    document.dispatchEvent(new CustomEvent(ThemeEvents.cartClear, { bubbles: true }));
  }

  /**
   * @param {boolean} isLoading
   * @param {HTMLButtonElement | null} submitter
   */
  #setLoading(isLoading, submitter) {
    this.#isSubmitting = isLoading;

    for (const button of [this.refs.subscriptionButton, this.refs.onetimeButton]) {
      if (!button) continue;

      button.disabled = isLoading;
      button.classList.toggle('is-loading', isLoading && button === submitter);
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (!this.refs.error) return;

    this.refs.error.textContent = message;
    this.refs.error.classList.remove('hidden');
  }

  #hideError() {
    if (!this.refs.error) return;

    this.refs.error.textContent = '';
    this.refs.error.classList.add('hidden');
  }

  /**
   * Syncs the product-info block of the section with the selected option — the same elements
   * the other subscription forms update. Prices come preformatted from Liquid.
   * @param {HTMLElement} option
   */
  #updateProductInfo(option) {
    if (!this.parent) return;

    const { infoPrice, infoComparePrice, infoHasCompare, infoServings, infoPerServing, discount } = option.dataset;

    for (const info of this.parent.querySelectorAll('.product-info')) {
      const prices = info.querySelector('.product-info__prices');

      if (prices) {
        const currentEl = prices.querySelector('.product-info__price--current');
        const oldEl = prices.querySelector('.product-info__price--old');

        if (currentEl && infoPrice) currentEl.textContent = infoPrice;
        if (oldEl && infoComparePrice) oldEl.textContent = infoComparePrice;

        prices.classList.toggle('hide-old-price', infoHasCompare !== 'true');
      }

      const discountValue = Number(discount) || 0;

      for (const el of info.querySelectorAll('[data-discount-target]')) {
        el.textContent = String(discountValue);
        el.parentElement?.classList.toggle('hidden', discountValue === 0);
      }

      if (infoServings) {
        for (const el of info.querySelectorAll('[data-servings-target]')) {
          el.textContent = infoServings;
        }
      }

      if (infoPerServing) {
        for (const el of info.querySelectorAll('[data-per-serving-target]')) {
          el.textContent = infoPerServing;
        }
      }
    }
  }

  /**
   * Same markers as the other subscription forms, so it works with both galleries.
   * @param {HTMLElement} option
   */
  #updateFirstImage(option) {
    const firstImageUrl = option.dataset.firstImage;
    if (!firstImageUrl || !this.parent) return;

    const img = this.parent.querySelector('[data-first-slide-image] img');
    if (!img) return;

    img.src = firstImageUrl;
    img.srcset = firstImageUrl;
    img.setAttribute('data_max_resolution', firstImageUrl);

    const thumbnailImg = this.parent.querySelector('[data-first-thumbnail-image] img');

    if (thumbnailImg) {
      thumbnailImg.src = firstImageUrl;
      thumbnailImg.srcset = firstImageUrl;
    }
  }
}

if (!customElements.get('subscription-form-v6')) {
  customElements.define('subscription-form-v6', SubscriptionFormV6);
}
